/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { Formik } from 'formik'
import { validateCNPJ } from 'validations-br'

import * as Yup from 'yup'

import { Typography, Button } from '../../../components'
import Input from '../../../components/Form/Input'
import {
  Form,
  FormBody,
  FormFooter,
  FormGroupContainer
} from '../../../components/Form/styles'
import useActions from '../../../hooks/useActions'
import { signUpActions } from '../../../redux/ducks/signUp'
import cepService from '../../../services/cep'

import * as S from '../styles'
import { CheckboxContainer, TextTermsAndConditions } from './styles'
import Select from '../../../components/Form/Select'

import { stateListForSelect } from './constants'
import useScrollToTop from '../../../hooks/useScrollToTop'
import { useSelector } from 'react-redux'
import { useDebounce } from '../../../hooks/useDebounce'

import { isAvailableURL } from '../../../helpers/isAvailableURL'
import { isAvailableCNPJ } from '../../../helpers/isAvailableCNPJ'
import Checkbox from '../../../components/Form/Checkbox'
import { useModal } from '../../../hooks/useModal'
import TermsAndConditionsModal from './TermsAndConditionsModal'
import { zipCodeValidate } from '../../../utils/zipCodeValidate'
import { ConfigurationsKeys } from '../../../constants/adminEcbrConfigurations'
import ecbrApi from '../../../services/ecbrApi'
import { ModalBodyContainer } from '../../../modals/styles'
import { useQuery } from 'react-query'

const StepOne = ({ isNewBrand }) => {
  const { setStep1, nextStep } = useActions(signUpActions)
  const { step1 } = useSelector((state) => state.signUp)
  const { scrollToTop } = useScrollToTop()
  const getAddressByCep = useDebounce(cepService.getAddressByCep, 500)
  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false)
  const [submitVisible, setSubmitVisible] = useState(false)
  const [renderModal, openModal] = useModal(TermsAndConditionsModal)

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        url: Yup.string()
          .required('Campo obrigatório')
          .matches(
            /^(?:www\.)?([^:\/?\s]+)?\.([^:\/?\s]+)$/,
            'Endereço inválido'
          )
          .matches(/^[a-zA-Z0-9-_\.]+$/, 'Endereço inválido')
          .test(
            'url',
            'Website já cadastrado ou inválido',
            async (value) => (await isAvailableURL(value)) ?? true
          ),
        brandName: Yup.string()
          .required('Campo obrigatório')
          .max(50, 'Máximo de 50 caracteres')
          .matches(
            /^[^$%¨&#!?"]+$/,
            'Esse campo não aceita caracteres especiais'
          ),
        cnpj: Yup.string()
          .required('Campo obrigatório')
          .test('cnpj', 'CNPJ inválido', (value) => validateCNPJ(value))
          .test(
            'cnpj',
            'CNPJ já cadastrado',
            async (value) =>
              isNewBrand || ((await isAvailableCNPJ(value)) ?? true)
          ),
        razaoSocial: Yup.string()
          .required('Campo obrigatório')
          .max(50, 'Máximo de 50 caracteres')
          .matches(
            /^[a-zA-Z\s\u00C0-\u00FF]+$/,
            'Esse campo não aceita números ou caracteres especiais'
          ),
        zipCode: Yup.string()
          .required('Campo obrigatório')
          .test('zipCode', 'CEP inválido', (value) => !!zipCodeValidate(value)),
        address: Yup.string()
          .required('Campo obrigatório')
          .max(50, 'Máximo de 50 caracteres')
          .matches(/^[^\d]+$/i, 'Esse campo não aceita números'),
        number: Yup.string()
          .max(50, 'Máximo de 50 caracteres')
          .required('Campo obrigatório'),
        complement: Yup.string().max(50, 'Máximo de 50 caracteres'),
        city: Yup.string()
          .required('Campo obrigatório')
          .max(50, 'Máximo de 50 caracteres')
          .matches(/^[^\d]+$/i, 'Esse campo não aceita números'),
        state: Yup.string()
          .transform((_, obj) => obj?.uf ?? undefined)
          .max(50, 'Máximo de 50 caracteres')
          .required('Campo obrigatório'),
        country: Yup.string()
      }),
    [isNewBrand]
  )

  const { data: term } = useQuery('terms', async () => {
    const { data } = await ecbrApi.configs.getByKey(ConfigurationsKeys.TERMS)
    return data.value ?? ''
  })

  const handleClickTermsAndConditions = () => {
    if (submitVisible) {
      setSubmitVisible(false)
      return
    }
    openModal({
      acceptTerm,
      hasContent: !!term,
      body: (
        <ModalBodyContainer>
          <Typography type="text" color="#7D7987" preLine>
            {term}
          </Typography>
        </ModalBodyContainer>
      )
    })
  }

  const acceptTerm = () => {
    setSubmitVisible(true)
    cloModal()
  }

  const cloModal = () => {
    openModal({ show: false })
  }

  const initialValues = useMemo(() => {
    const { state, ...rest } = step1
    return {
      ...rest,
      state: stateListForSelect.find((item) => item.uf === state)
    }
  }, [step1])

  useEffect(() => {
    scrollToTop()
  }, [scrollToTop])

  const onSubmit = async ({ state, ...rest }, { setSubmitting }) => {
    setSubmitting(true)
    const values = {
      ...rest,
      state: state.uf
    }
    await setStep1(values)
    setSubmitting(false)
    nextStep()
  }

  const handleChangeZipCode = useCallback(
    async (zipCode, setFieldValue) => {
      if (zipCode.replace('_', '').length === 9) {
        console.log('Consultando serviço de CEP...')
        setIsLoadingZipCode(true)
        try {
          const address = await getAddressByCep(zipCode)
          const state = stateListForSelect.find((s) => {
            return s.uf === address.uf
          })
          if (address.logradouro) {
            setFieldValue('address', address.logradouro)
            setFieldValue('city', address.localidade)
            state && setFieldValue('state', state)
          }
          return address.logradouro
        } catch (err) {
          console.log({ err })
        } finally {
          setIsLoadingZipCode(false)
        }
      }
    },
    [getAddressByCep]
  )

  return (
    <>
      <Typography type="text" color="#7D7987" spacing="32px 0 0 0">
        Precisamos de alguns dados para o cadastro
      </Typography>
      <S.StepOneContainer>
        <S.LeftContent>
          <S.InfoCard>
            <Typography type="text" color="#7D7987">
              URL do ecomm
            </Typography>
            <Typography type="text" color="#7D7987">
              certificado
            </Typography>
          </S.InfoCard>
          {!isNewBrand && (
            <S.InfoCard>
              <Typography type="text" color="#7D7987">
                dados da empresa
              </Typography>
              <Typography type="text" color="#7D7987">
                controladora
              </Typography>
            </S.InfoCard>
          )}
        </S.LeftContent>
        <S.RightContent>
          <Formik
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={onSubmit}
            validateOnChange={false}
          >
            {({ setFieldValue }) => (
              <Form>
                <FormBody>
                  <Typography
                    type="text"
                    color="#BE9137"
                    spacing="0 0 1rem 0"
                    align="center"
                  >
                    Dados da certificação
                  </Typography>
                  <Input
                    name="url"
                    label="URL (endereço do site)"
                    showValidIcon
                  />
                  <Input
                    name="brandName"
                    label="Nome do e-commerce"
                    showValidIcon
                    type="text"
                  />
                  <FormGroupContainer
                    display={isNewBrand ? 'none' : false}
                    type="flex"
                    direction="column"
                  >
                    <Typography
                      type="text"
                      color="#BE9137"
                      spacing="1rem 0 1rem 0"
                      align="center"
                    >
                      Dados da empresa
                    </Typography>
                    <Input
                      name="cnpj"
                      label="CNPJ"
                      showValidIcon
                      mask="99.999.999/9999-99"
                    />
                    <Input
                      name="razaoSocial"
                      label="Razão Social"
                      showValidIcon
                    />
                    <Input
                      name="zipCode"
                      label="CEP"
                      showValidIcon
                      mask="99999-999"
                      onChange={(value) =>
                        handleChangeZipCode(value, setFieldValue)
                      }
                      isLoading={isLoadingZipCode}
                    />
                    <Input
                      name="address"
                      label="Endereço"
                      showValidIcon
                      display="inline-block"
                    />
                    <FormGroupContainer>
                      <Input
                        name="number"
                        label="Número"
                        showValidIcon
                        display="inline-block"
                      />
                      <Input
                        name="complement"
                        label="Complemento"
                        showValidIcon
                        display="inline-block"
                      />
                    </FormGroupContainer>
                    <Input
                      name="city"
                      label="Cidade"
                      showValidIcon
                      display="inline-block"
                    />
                    <Select
                      name="state"
                      label="Estado"
                      options={stateListForSelect}
                      optionValue="uf"
                      optionLabel="name"
                    />
                  </FormGroupContainer>
                  <CheckboxContainer>
                    <Checkbox
                      onClick={handleClickTermsAndConditions}
                      name="acceptTerms"
                      overflow={'visible'}
                      checked={submitVisible}
                    />
                    <TextTermsAndConditions
                      onClick={handleClickTermsAndConditions}
                    >
                      Concordar com termos de aceite
                    </TextTermsAndConditions>
                  </CheckboxContainer>
                </FormBody>
                <FormFooter>
                  <Button
                    text="PROSSEGUIR"
                    type="submit"
                    variant="primary"
                    width="200px"
                    disabled={!submitVisible}
                  />
                </FormFooter>
              </Form>
            )}
          </Formik>
          {renderModal}
        </S.RightContent>
      </S.StepOneContainer>
    </>
  )
}

export default StepOne
